import React, { useCallback, useEffect, useMemo, useState } from "react";
import isNil from "lodash/isNil";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { LINK_KIND_APPLICATION } from "../../hooks/useApplicationLinksHook";
import PublicFormShell from "../../components/publicForm/PublicFormShell";
import PublicFormSuccess from "../../components/publicForm/PublicFormSuccess";
import DraftRestoreBanner from "../../components/publicForm/DraftRestoreBanner";
import usePublicFormDraft, {
  ssnSubmitCheck,
} from "../../components/publicForm/usePublicFormDraft";
import usePublicLinkPrefill from "../../components/publicForm/usePublicLinkPrefill";
import PublicLinkUnavailable from "../../components/publicForm/PublicLinkUnavailable";
import DevPrefillBar from "../../components/publicForm/DevPrefillBar";
import { buildDevApplicationValues } from "./devPrefillValues";
import StepBackgroundExperience from "./steps/StepBackgroundExperience";
import StepBorrowerDetails from "./steps/StepBorrowerDetails";
import StepBorrowingEntities from "./steps/StepBorrowingEntities";
import StepConfirmation from "./steps/StepConfirmation";
import StepLoanDetails from "./steps/StepLoanDetails";
import StepProperties from "./steps/StepProperties";
import StepReview from "./steps/StepReview";
import { LOAN_CONTACT_ROLES } from "./applicationFormShared";

/**
 * Public, unauthenticated loan application (`/loan-application`).
 *
 * Mounted OUTSIDE ProtectedRoute — there is no session here, so nothing in this
 * tree may read Redux user state or call an authenticated endpoint. Submission
 * goes to `POST borrower_submissions/new_application`, which accepts anonymous
 * posts.
 *
 * Distinct from the broker-facing `/create-loan` flow: this is the form a broker
 * sends to their borrower to fill out directly.
 *
 * Query params:
 *   ?hideLogo=true       — render without the partner logo (for embedding)
 *   ?brandingName=<name> — render a custom logo name instead
 *
 * Field names throughout are a wire contract with the backend; see
 * applicationFormShared.js.
 */

// Must not collide with the credit-authorization form's key — same origin.
const DRAFT_STORAGE_KEY = "brokerPortal.loanApplication.draft.v1";

// `sublabel` is the rail's one-liner; `title`/`description` head the step body.
const STEPS = [
  {
    label: "Borrower & Loan",
    sublabel: "About you and the loan",
    title: "Let's start with you",
    description:
      "Tell us who's applying and what you're borrowing for. Your product and purpose tailor the rest of the application.",
    Component: StepBorrowerDetails,
    submitCheck: ssnSubmitCheck,
  },
  {
    label: "Background & Experience",
    sublabel: "Liquidity, credit, track record",
    title: "Your background and experience",
    description:
      "This helps us size the deal and understand the projects you've taken on before.",
    Component: StepBackgroundExperience,
  },
  {
    label: "Loan Details",
    sublabel: "Amount and contacts",
    title: "The numbers",
    description:
      "How much you need, what we're paying off, and anyone already working on the deal.",
    Component: StepLoanDetails,
  },
  {
    label: "Properties",
    sublabel: "What's being financed",
    title: "The properties",
    description:
      "Add every property this loan will cover. Each one can be expanded and edited independently.",
    Component: StepProperties,
  },
  {
    label: "Borrowing Entity",
    sublabel: "Who holds the loan",
    title: "The borrowing entity",
    description:
      "Loans are made to an entity rather than an individual. Tell us about yours, existing or planned.",
    Component: StepBorrowingEntities,
  },
  {
    label: "Review",
    sublabel: "Check your answers",
    title: "Review your application",
    description:
      "Everything you've entered so far. Change any answer right here — you don't need to go back through the form.",
    Component: StepReview,
  },
  {
    label: "Sign & Submit",
    sublabel: "Files and signature",
    title: "Sign and submit",
    description:
      "Attach anything else that supports the application, then read and sign the authorization below.",
    Component: StepConfirmation,
  },
];

/**
 * `firstTimeHomebuyer` is the field the backend reads, but it's no longer asked
 * directly — the form asks whether the applicant owns or rents today, and (for
 * renters) whether they've ever owned. Owning a home now, or having owned one
 * before, both mean "not a first-time buyer".
 *
 * Returns undefined rather than guessing when the questions are unanswered.
 */
const deriveFirstTimeHomebuyer = ({
  primaryResidenceOwnership,
  hasEverOwnedPrimaryResidence,
}) => {
  if (primaryResidenceOwnership === "Own") return false;
  if (primaryResidenceOwnership === "Rent") {
    if (hasEverOwnedPrimaryResidence === true) return false;
    if (hasEverOwnedPrimaryResidence === false) return true;
  }
  return undefined;
};

const LoanApplicationFormScreen = ({
  initialValues,
  hideLogo: hideLogoProp = false,
}) => {
  const [success, setSuccess] = useState(false);
  const { loading, createApplication } = useBorrowerSubmissionsHook();

  // The prop sets the baseline (the /unbranded route); ?hideLogo=true can turn
  // it on for the plain route but never turns the prop back off.
  const [hideLogo, setHideLogo] = useState(hideLogoProp);
  const [customLogoName, setCustomLogoName] = useState(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      setHideLogo(hideLogoProp || params.get("hideLogo") === "true");
      setCustomLogoName(params.get("brandingName") || null);
    } catch (e) {
      setHideLogo(hideLogoProp);
      setCustomLogoName(null);
    }
  }, [hideLogoProp]);

  // Draft persistence is only meaningful for a fresh application; when the
  // caller seeds initialValues we're editing something specific, so leave it off.
  const draftEnabled = isNil(initialValues);
  const { restored, checked, save, clear, dismissRestore } = usePublicFormDraft({
    storageKey: DRAFT_STORAGE_KEY,
    enabled: draftEnabled,
  });
  const [draftChoice, setDraftChoice] = useState(null);

  // Local-dev prefill. Held as the values themselves rather than a flag so the
  // Files and the drawn signature are built once, at click time — rebuilding
  // them on every render would hand react-final-form a new `initialValues`
  // identity each pass.
  const [devValues, setDevValues] = useState(null);

  // `?t=<token>` (or a migrated `?link_id=`) — resolved server-side into prefill.
  const {
    prefill,
    checked: linkChecked,
    linkStatus,
    token,
    linkId,
    reportStarted,
    hasUrlParams,
  } = usePublicLinkPrefill(LINK_KIND_APPLICATION);

  // Start with a single blank property card so the applicant never lands on an
  // empty Properties step. A restored draft is the applicant's own work and so
  // outranks the broker's prefill.
  const init = useMemo(() => {
    // Dev prefill outranks everything — it exists precisely to blow past
    // whatever state the form would otherwise open in.
    if (!isNil(devValues)) return devValues;
    if (!isNil(initialValues)) return initialValues;
    const base = { subjectProperties: [{}], ...(prefill || {}) };

    // A broker who filled in the entity name has answered the gating question
    // implicitly — an entity you can name is one you have. Leaving it blank
    // hides the very fields the prefilled name belongs to, so the applicant
    // opens the step to a Yes/No and no sign their entity came through.
    // Only inferred, never overridden: an explicit false in the prefill stands.
    if (!isNil(base.entityName) && isNil(base.applicantHasExistingEntity)) {
      base.applicantHasExistingEntity = true;
    }

    if (draftChoice === "restored" && restored?.values) {
      return { ...base, ...restored.values };
    }
    return base;
  }, [initialValues, draftChoice, restored, prefill, devValues]);

  const onFormSubmit = (data) => {
    // Contacts are collected as three positional slots; stamp each with its role
    // and drop the ones the applicant left entirely blank.
    const loanRelatedContacts = (data.loanRelatedContacts || [])
      .map((contact, i) => ({ ...contact, loanRole: LOAN_CONTACT_ROLES[i] }))
      .filter(({ loanRole, ...fields }) =>
        Object.values(fields).some((val) => !isNil(val) && val !== ""),
      );

    createApplication({
      payload: {
        ...data,
        loanRelatedContacts,
        firstTimeHomebuyer: deriveFirstTimeHomebuyer(data),
      },
      // Identifies the link this submission came through — the server attributes
      // it to the sending LO and closes the funnel on it.
      token,
      linkId,
      onSuccessFn: () => {
        clear();
        setSuccess(true);
      },
    });
  };

  const handleValuesChange = useCallback(
    (values, step) => {
      reportStarted();
      if (draftEnabled) save(values, step);
    },
    [save, draftEnabled, reportStarted],
  );

  if (success) {
    return (
      <PublicFormSuccess
        hideLogo={hideLogo}
        customLogoName={customLogoName}
        title="Application received"
        message="Thank you — your loan application has been submitted to our team."
        nextSteps={[
          "Our team reviews your application and the documents you uploaded.",
          "We'll reach out at the email address you provided if we need anything else.",
          "You'll hear from us with next steps on your financing request.",
        ]}
      />
    );
  }

  // A link that resolved to anything but "ok" must not show the form: the
  // submit would 410 after the borrower had filled it all in. `null` is the
  // no-link case (a direct visit), which still gets a blank form.
  if (linkChecked && linkStatus && linkStatus !== "ok") {
    return <PublicLinkUnavailable status={linkStatus} hideLogo={hideLogo} />;
  }

  // Hold the form back until the draft and link lookups resolve — mounting with
  // empty values and then swapping initialValues underneath would reset the form.
  if (!linkChecked) return null;
  if (draftEnabled && !checked) return null;

  // Only ever offered on a plain visit. A URL carrying params arrived from
  // somewhere — a sent link, an embed with context — and its prefill is the
  // answer the applicant should be looking at, not a stale draft from a previous
  // visit. Asking them to choose between the two invites picking the wrong one.
  // The draft is still saved either way; a later plain visit can still restore it.
  const showDraftBanner =
    Boolean(restored) && isNil(draftChoice) && !hasUrlParams;

  return (
    <PublicFormShell
      // Remount on restore (and on a dev prefill) so react-final-form picks up
      // the new initialValues — it reads them once, at mount.
      key={devValues ? "dev-prefill" : draftChoice || "fresh"}
      steps={STEPS}
      onSubmit={onFormSubmit}
      initialValues={init}
      initialStep={draftChoice === "restored" ? (restored?.step ?? 0) : 0}
      loading={loading}
      hideLogo={hideLogo}
      customLogoName={customLogoName}
      formTitle="Loan Application"
      submitLabel="Submit Application"
      onValuesChange={handleValuesChange}
      devTools={
        <DevPrefillBar
          onPrefill={() => setDevValues(buildDevApplicationValues())}
          note="Local only — fills all seven steps, including the photo ID and signature, so you can click straight to submit."
        />
      }
      draftBanner={
        showDraftBanner ? (
          <DraftRestoreBanner
            savedAt={restored?.savedAt}
            onRestore={() => setDraftChoice("restored")}
            onDiscard={() => {
              clear();
              setDraftChoice("discarded");
            }}
            // Keeps the saved answers, just stops asking — here and on every
            // future visit from this browser.
            onIgnore={() => {
              dismissRestore();
              setDraftChoice("ignored");
            }}
          />
        ) : null
      }
    />
  );
};

export default LoanApplicationFormScreen;
